import { Account, ACCOUNT_SUBSCRIPTION_TYPE } from '@adtrackify/at-tracking-event-types';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useAccountState } from '@/contexts/account/account-context';
import { usePixelDestinationsLoading } from '@/features/workspace/providers/account-stores-context';
import {
  OnboardingAcknowledgement,
} from '@/features/setup/types/use-acknowledge-onboarding-step-enums';
import { useBackfillOnboardingAcknowledgements } from '@/features/setup/hooks/use-backfill-onboarding-acknowledgements';
import { usePixelDestinations } from '@/hooks/account/use-pixel-destinations';
import { useOnboardingFlowStore } from '@/features/setup/stores/use-onboarding-flow-store';
import { OnboardingBusinessType, OnboardingPlatform } from '@/features/setup/types/use-onboarding-flow-store-enums';

import { checkDestinationConnections } from './use-destination-status';
import { checkIntegrationConnections } from '../helpers/check-integration-connections';

const ONBOARDING_LAUNCH_DATE = new Date('2026-03-07T00:00:00Z');

const LADDER_ACK_STEPS_LAUNCH_DATE = new Date('2026-07-30T00:00:00Z');

function isAccountCreatedBefore(createdAt: string | undefined, launchDate: Date): boolean {
  return !!createdAt && new Date(createdAt) < launchDate;
}

function isShopifyAppSubscription(account: Account): boolean {
  return (
    account.accountSubscriptionType === ACCOUNT_SUBSCRIPTION_TYPE.SHOPIFY &&
    !(account.isPlatformMigrated ?? false)
  );
}

export function useAutoDetectOnboardingComplete(): void {
  const hasRunForAccount = useRef<string | null>(null);
  const hasSetShopifyAppCustomerForAccount = useRef<string | null>(null);
  const hasSetPlatformCustomerForAccount = useRef<string | null>(null);
  const hasBackfilledAcksForAccount = useRef<string | null>(null);

  const { currentAccount, accountId } = useAccountState();

  const pixelDestinations = usePixelDestinations();
  const { isSuccess: destinationsLoaded } = usePixelDestinationsLoading();
  const { mutate: backfillAcknowledgements } = useBackfillOnboardingAcknowledgements();

  const {
    businessType,
    dismissed,
    setBusinessTypeAndPlatform,
    setIsShopifyAppCustomer,
    setDataSourceConnected,
    setDataSourceConnection,
    setDestinationConnection,
    setTrackingInstalled,
    setFirstEventReceived,
    setTimezoneConfirmed,
    dismiss,
  } = useOnboardingFlowStore(
    useShallow((s) => ({
      businessType: s.businessType,
      dismissed: s.dismissed,
      setBusinessTypeAndPlatform: s.setBusinessTypeAndPlatform,
      setIsShopifyAppCustomer: s.setIsShopifyAppCustomer,
      setDataSourceConnected: s.setDataSourceConnected,
      setDataSourceConnection: s.setDataSourceConnection,
      setDestinationConnection: s.setDestinationConnection,
      setTrackingInstalled: s.setTrackingInstalled,
      setFirstEventReceived: s.setFirstEventReceived,
      setTimezoneConfirmed: s.setTimezoneConfirmed,
      dismiss: s.dismiss,
    }))
  );

  // Always auto-complete onboarding for Shopify app customers based on account subscription type.
  // This runs independently of the main onboarding detection to ensure Shopify app customers:
  // 1. NEVER see the "Connect Shopify" step
  // 2. Have business type set to E-commerce + Shopify
  // 3. Have onboarding dismissed (synced to backend via useSyncOnboardingToBackend)
  useEffect(() => {
    if (!currentAccount || !accountId) {
      return;
    }

    // Skip if already processed for this account
    if (hasSetShopifyAppCustomerForAccount.current === accountId) {
      return;
    }

    if (isShopifyAppSubscription(currentAccount)) {
      hasSetShopifyAppCustomerForAccount.current = accountId;

      // Mark as Shopify app customer (hides "Connect Shopify" step)
      setIsShopifyAppCustomer(true);

      // Set business type to E-commerce + Shopify
      setBusinessTypeAndPlatform(
        OnboardingBusinessType.ECOMMERCE,
        OnboardingPlatform.SHOPIFY
      );

      // Mark data source as connected (Shopify is connected via app install)
      setDataSourceConnected(true);

      // Legacy customer — their timezone synced from Shopify on install; don't
      // regress them to "Setup incomplete" just because the new confirm-step
      // exists.
      setTimezoneConfirmed(true);

      // Dismiss onboarding - this will be synced to backend via useSyncOnboardingToBackend
      dismiss();
    }
  }, [
    currentAccount,
    accountId,
    setIsShopifyAppCustomer,
    setBusinessTypeAndPlatform,
    setDataSourceConnected,
    setTimezoneConfirmed,
    dismiss,
  ]);

  // Auto-complete onboarding for existing Platform customers (pre-March 7th) with Shopify integration.
  // This handles:
  // 1. Platform customers with accountSubscriptionType = 'platform'
  // 2. Shopify customers who migrated to platform (isPlatformMigrated = true)
  // Criteria: created before March 7th AND has Shopify integration connected
  useEffect(() => {
    if (!currentAccount || !accountId || dismissed) {
      return;
    }

    // Skip if already processed for this account
    if (hasSetPlatformCustomerForAccount.current === accountId) {
      return;
    }

    if (!isAccountCreatedBefore(currentAccount.createdAt, ONBOARDING_LAUNCH_DATE)) {
      return;
    }

    const subscriptionType = currentAccount.accountSubscriptionType;
    const isPlatformMigrated = currentAccount.isPlatformMigrated ?? false;
    const isPlatformCustomer =
      subscriptionType === ACCOUNT_SUBSCRIPTION_TYPE.PLATFORM || isPlatformMigrated;

    if (!isPlatformCustomer) {
      return;
    }

    const integrations = checkIntegrationConnections(pixelDestinations);

    // Only auto-complete if Shopify integration is connected
    if (integrations.isShopifyConnected) {
      hasSetPlatformCustomerForAccount.current = accountId;

      // Set business type to E-commerce + Shopify
      setBusinessTypeAndPlatform(
        OnboardingBusinessType.ECOMMERCE,
        OnboardingPlatform.SHOPIFY
      );

      // Mark Shopify store as connected
      setDataSourceConnected(true);

      // Mark tracking pixel as installed (they have Shopify integration)
      setTrackingInstalled(true);

      setTimezoneConfirmed(true);

      // Dismiss onboarding - this will be synced to backend via useSyncOnboardingToBackend
      dismiss();
    }
  }, [
    currentAccount,
    accountId,
    pixelDestinations,
    dismissed,
    setBusinessTypeAndPlatform,
    setDataSourceConnected,
    setTrackingInstalled,
    setTimezoneConfirmed,
    dismiss,
  ]);

  useEffect(() => {
    // Wait for account to load
    if (!currentAccount || !accountId) {
      return;
    }

    // Only run once per account and only if not already set up
    if (hasRunForAccount.current === accountId || businessType !== null) {
      return;
    }

    // Only auto-complete for existing accounts created before onboarding was introduced
    // New accounts should go through the full onboarding flow
    const accountCreatedAt = currentAccount.createdAt
      ? new Date(currentAccount.createdAt)
      : null;

    // New accounts should go through normal onboarding
    // Note: isShopifyAppCustomer and dataSourceConnected are handled by the separate effect above
    if (!isAccountCreatedBefore(currentAccount.createdAt, ONBOARDING_LAUNCH_DATE)) {
      // For new Shopify app customers created before yesterday,
      // assume they already have tracking installed and dismiss the onboarding
      if (isShopifyAppSubscription(currentAccount)) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);

        if (accountCreatedAt && accountCreatedAt < yesterday) {
          hasRunForAccount.current = accountId;
          setTrackingInstalled(true);
          setTimezoneConfirmed(true);
          dismiss();
        }
      }
      return;
    }

    const subscriptionType = currentAccount.accountSubscriptionType;
    const isPlatformMigrated = currentAccount.isPlatformMigrated ?? false;
    const isShopifyCustomer = isShopifyAppSubscription(currentAccount);
    const isPlatformCustomer =
      subscriptionType === ACCOUNT_SUBSCRIPTION_TYPE.PLATFORM || isPlatformMigrated;

    // For existing Shopify customers, auto-complete their onboarding
    // They already have Shopify connected via their app install
    if (isShopifyCustomer) {
      const shopifyAppInstallId = currentAccount.subscription?.shopifyAppInstallId;

      if (shopifyAppInstallId) {
        hasRunForAccount.current = accountId;

        // Mark as Shopify app customer (affects which steps are shown)
        setIsShopifyAppCustomer(true);

        // Set as E-commerce + Shopify
        setBusinessTypeAndPlatform(
          OnboardingBusinessType.ECOMMERCE,
          OnboardingPlatform.SHOPIFY
        );

        // Shopify app customers have Shopify connected by definition
        setDataSourceConnected(true);

        // Shopify app customers have the pixel installed via the app
        setTrackingInstalled(true);

        // Check for ad integrations and destinations
        const integrations = checkIntegrationConnections(pixelDestinations);
        const destinations = checkDestinationConnections(pixelDestinations);

        if (integrations.isFacebookAdsConnected) {
          setDataSourceConnection('facebookAds', true);
        }
        if (integrations.isGoogleAdsConnected) {
          setDataSourceConnection('googleAds', true);
        }
        if (destinations.isMetaConnected) {
          setDestinationConnection('metaCapi', true);
        }
        if (destinations.isGoogleConnected) {
          setDestinationConnection('googleAdsCapi', true);
        }
        if (destinations.isTiktokConnected) {
          setDestinationConnection('tiktokEventsApi', true);
        }
        if (destinations.isKlaviyoConnected) {
          setDestinationConnection('klaviyo', true);
        }

        // Existing Shopify customers have events flowing
        setFirstEventReceived(true);

        setTimezoneConfirmed(true);

        // Auto-dismiss for existing customers so they don't see a completed checklist
        dismiss();

        return;
      }
    }

    // For Platform customers, check if they have Shopify connected as an integration
    if (isPlatformCustomer && pixelDestinations.length > 0) {
      const integrations = checkIntegrationConnections(pixelDestinations);
      const destinations = checkDestinationConnections(pixelDestinations);

      // Only auto-complete if they have Shopify integration connected
      if (integrations.isShopifyConnected) {
        hasRunForAccount.current = accountId;

        // Set as E-commerce + Shopify
        setBusinessTypeAndPlatform(
          OnboardingBusinessType.ECOMMERCE,
          OnboardingPlatform.SHOPIFY
        );

        // Mark Shopify as connected
        setDataSourceConnected(true);

        // Platform customers with Shopify integration have the pixel installed
        setTrackingInstalled(true);

        // Sync individual data source connections
        if (integrations.isFacebookAdsConnected) {
          setDataSourceConnection('facebookAds', true);
        }
        if (integrations.isGoogleAdsConnected) {
          setDataSourceConnection('googleAds', true);
        }

        // Sync individual destination connections
        if (destinations.isMetaConnected) {
          setDestinationConnection('metaCapi', true);
        }
        if (destinations.isGoogleConnected) {
          setDestinationConnection('googleAdsCapi', true);
        }
        if (destinations.isTiktokConnected) {
          setDestinationConnection('tiktokEventsApi', true);
        }
        if (destinations.isKlaviyoConnected) {
          setDestinationConnection('klaviyo', true);
        }

        // Check if they have events flowing
        if (destinations.anyConnected || integrations.anyDataSourceConnected) {
          setFirstEventReceived(true);
        }

        setTimezoneConfirmed(true);

        // Auto-dismiss for existing customers so they don't see a completed checklist
        dismiss();
      }
    }
  }, [
    accountId,
    businessType,
    pixelDestinations,
    setBusinessTypeAndPlatform,
    setIsShopifyAppCustomer,
    setDataSourceConnected,
    setDataSourceConnection,
    setDestinationConnection,
    setTrackingInstalled,
    setFirstEventReceived,
    setTimezoneConfirmed,
    dismiss,
    currentAccount,
  ]);

  // The two ladder acknowledgement steps landed after these accounts onboarded,
  // so nobody ever asked them to confirm. Waive each step from the connection it
  // sits behind — Shopify for data sharing, Klaviyo for flows — instead of
  // surfacing setup work they already did with us.
  //
  // These write to `account.onboarding` rather than the flow store: the ladder
  // reads acknowledgements straight off the account, and the store's sync
  // snapshot doesn't carry these fields.
  useEffect(() => {
    if (!currentAccount || !accountId) {
      return;
    }

    // Skip if already processed for this account
    if (hasBackfilledAcksForAccount.current === accountId) {
      return;
    }

    if (!isAccountCreatedBefore(currentAccount.createdAt, LADDER_ACK_STEPS_LAUNCH_DATE)) {
      return;
    }

    // Wait for destinations to finish loading before deciding which steps to
    // waive. Shopify can be detected via subscription type before destinations
    // resolve, but Klaviyo requires the destination list — processing early
    // would write only Shopify and skip Klaviyo forever (once-per-account guard).
    if (!destinationsLoaded) {
      return;
    }

    const onboarding = currentAccount.onboarding ?? {};
    const integrations = checkIntegrationConnections(pixelDestinations);
    const destinations = checkDestinationConnections(pixelDestinations);
    const isShopifyConnected =
      integrations.isShopifyConnected || isShopifyAppSubscription(currentAccount);

    const fields: OnboardingAcknowledgement[] = [];
    if (isShopifyConnected && !onboarding.shopifyDataSharingDisabled) {
      fields.push(OnboardingAcknowledgement.SHOPIFY_DATA_SHARING_DISABLED);
    }
    if (destinations.isKlaviyoConnected && !onboarding.klaviyoFlowsConfigured) {
      fields.push(OnboardingAcknowledgement.KLAVIYO_FLOWS_CONFIGURED);
    }

    // Nothing to waive — no connections detected.
    if (fields.length === 0) {
      return;
    }

    hasBackfilledAcksForAccount.current = accountId;
    backfillAcknowledgements(fields);
  }, [currentAccount, accountId, pixelDestinations, destinationsLoaded, backfillAcknowledgements]);
}
